/* eslint-disable complexity */

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ChartOfAccountsAccount } from '@futrli-sdk/predict';

import { PAYMENT_TYPES } from '../../../core/constants';
import { AllowedAssumptionTypes, AllowedPaymentTypes } from '../../detail/reports/actions/actions';
import {
  AllowedConsolidationAssumptionTypes,
  AllowedConsolidationPaymentTypes,
  PredictionType,
  paymentTreatmentTypes
} from '../types';
import { getPaymentData, isCashFlowTreatmentDisabled } from '../context/utils';
import { useCashFlowTreatment } from './use-cash-flow-treatment';

type AllAllowedAssumptionTypes = AllowedAssumptionTypes | AllowedConsolidationAssumptionTypes;

export const usePredictionBuilderFields = (
  predictionData?: AllAllowedAssumptionTypes,
  predictionPaymentData?: AllowedPaymentTypes | AllowedConsolidationPaymentTypes,
  predictionPaymentType?: PAYMENT_TYPES,
  PredictionTypeFromData: PredictionType = PredictionType.FREESTYLE
) => {
  const [accountIndex, setAccountIndex] = useState('');
  const [predictionContentTouched, setPredictionContentTouched] = useState(false);
  const [predictionName, setPredictionName] = useState('');
  const [predictionType, setPredictionType] = useState<PredictionType>(PredictionTypeFromData);
  const [vatPercentage, setVatPercentage] = useState<string>('');

  const [impactPreviewParams, setImpactPreviewParams] = useState<Partial<AllAllowedAssumptionTypes>>();
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);

  const {
    cashFlowImpactTouched,
    handleAddNewMultiPaymentTreatment,
    handleChangeMultiPaymentTreatmentData,
    handleNonCashAccountIndexChange,
    handlePaymentTermsChange,
    handlePaymentTreatmentChange,
    multiPaymentData,
    nonCashAccountIndex,
    paymentTerms,
    paymentTreatment,
    reset: resetCashFlowImpact,
    restore: restoreCashFlowImpact
  } = useCashFlowTreatment();

  const isCashFlowDisabled = isCashFlowTreatmentDisabled(
    paymentTreatment,
    paymentTerms,
    multiPaymentData,
    nonCashAccountIndex,
    accountIndex
  );
  const isInvalid = !predictionName || !accountIndex || isCashFlowDisabled;

  const paymentData = useMemo(() => {
    return getPaymentData(paymentTreatment, paymentTerms, multiPaymentData, nonCashAccountIndex);
  }, [multiPaymentData, nonCashAccountIndex, paymentTerms, paymentTreatment]);

  const paymentType = paymentTreatmentTypes[paymentTreatment];

  // *********
  // * Utils *
  // *********

  const reset = useCallback(() => {
    setImpactPreviewParams(undefined);

    setAccountIndex('');
    setPredictionName('');
    setPredictionType(PredictionType.FREESTYLE);
    setVatPercentage('');
    setPredictionContentTouched(false);

    resetCashFlowImpact();

    setPredictionContentTouched(false);
  }, [resetCashFlowImpact]);

  const updatePaymentTreatment = useCallback(() => {
    if (predictionData && predictionPaymentData) {
      restoreCashFlowImpact(predictionPaymentData, predictionPaymentType);
    }
  }, [predictionData, predictionPaymentData, predictionPaymentType, restoreCashFlowImpact]);

  const updatePredictionStateData = useCallback(() => {
    if (predictionData) {
      setPredictionName(predictionData.name || '');
      setVatPercentage(predictionData.vat_percentage || '');
      setAccountIndex(predictionData.account_id);
    }
  }, [predictionData]);

  const updatePredictionType = useCallback(() => {
    if (PredictionTypeFromData) {
      setPredictionType(PredictionTypeFromData);
    }
  }, [PredictionTypeFromData]);

  // ************
  // * Handlers *
  // ************

  const handleAccountIndexChange = useCallback((value: ChartOfAccountsAccount | null) => {
    setAccountIndex(value ? value.code : '');
  }, []);

  const handlePredictionNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPredictionName(e.target.value);
  }, []);

  const handlePredictionTypeChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newPredictionType = event.target.value as PredictionType;
    setPredictionType(newPredictionType);
    setPredictionContentTouched(true);
    setImpactPreviewParams(undefined);
  }, []);

  const handleVatPercentageChange = useCallback((value: string) => {
    setVatPercentage(value);
  }, []);

  // ***********
  // * Effects *
  // ***********

  // If payment data changes, update our payment treatment related data
  useEffect(() => {
    updatePaymentTreatment();
  }, [updatePaymentTreatment]);

  // If prediction data (fetched from api) changes, update various bits of the state (name, selected account etc)
  useEffect(() => {
    updatePredictionStateData();
  }, [updatePredictionStateData]);

  // If given prediction type changes, update state
  useEffect(() => {
    updatePredictionType();
  }, [updatePredictionType]);

  const values = {
    accountIndex,
    handleAccountIndexChange,
    handleAddNewMultiPaymentTreatment,
    handleChangeMultiPaymentTreatmentData,
    handleNonCashAccountIndexChange,
    handlePaymentTermsChange,
    handlePaymentTreatmentChange,
    handlePredictionNameChange,
    handlePredictionTypeChange,
    handleVatPercentageChange,
    impactPreviewParams,
    isCashFlowDisabled,
    isInvalid,
    isSaveDisabled: isSaveDisabled || isInvalid,
    multiPaymentData,
    nonCashAccountIndex,
    paymentData,
    paymentTerms,
    paymentTreatment,
    paymentType,
    predictionContentTouched: cashFlowImpactTouched || predictionContentTouched,
    predictionName,
    predictionType,
    reset,
    setAccountIndex,
    setImpactPreviewParams,
    setIsSaveDisabled,
    setPredictionContentTouched,
    setPredictionName,
    setPredictionType,
    setVatPercentage,
    vatPercentage
  };

  return values;
};
